<template>
  <ActivityResultForm
    v-if="goal && initialized"
    ref="form"
    :activity-object="activity"
    :goal-object="goal"
    :attachment-option="attachmentOption"
    @onClickSave="registerActivity"
    @onClickDraft="registerDraft" />
</template>

<script>
import { format } from 'date-fns'
import ActivityResultForm from '@/components/view/ActivityResultForm.vue'
import validate from '@/components/mixin/validate'
import confirmIfFormChanged from '@/components/mixin/confirmIfFormChanged'

export default {
  components: {
    ActivityResultForm
  },
  mixins: [
    validate,
    confirmIfFormChanged()
  ],
  data() {
    return {
      activity: {
        name: '',
        date: '',
        time: '',
        detail: '',
        result_count: 1,
        star: false,
        file: null,
        conditions: null,
        bodyPain: ''
      },
      goal: null,
      totalActivityCount: 0,
      file: null,
      attachmentOption: false,
      initialized: false
    }
  },
  computed: {
    memberId() {
      return this.$route.params.id
    },
    goalId() {
      return this.$route.params.goalId
    },
    achievementRate() {
      if (!this.goal) return ''
      return `${this.totalActivityCount} / ${this.goal.goalNumber}`
    },
    workAt() {
      return `${this.activity.date}T${this.activity.time}+09:00`
    },
    hasFile() {
      return !!this.file
    },
    conditionOptions() {
      return this.conditionContents.filter(content => this.goal.conditionSetting.items.includes(content.name))
    }
  },
  async created() {
    await this.init()
    if (this.goal.conditionSetting !== null) {
      this.activity.conditions = this.goal.conditionSetting.items.reduce((acc, key) => {
        acc[key] = null
        return acc
      }, {})
    }
    this.initialized = true
  },
  methods: {
    init() {
      this.setTodayDate()
      this.$store.commit('startLoading')
      return Promise.all([this.fetchGoal(), this.fetchTenantInfo()])
        .catch((e) => {
          console.error(e)
          this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        }).finally(() => {
          this.$store.commit('finishLoading')
        })
    },
    async fetchTenantInfo() {
      const tenantInfo = await this.$axios.get('/tenants/')
      this.attachmentOption = tenantInfo.data.attachmentOption
      return
    },
    setTodayDate() {
      const pretty = format(new Date(), 'yyyy-MM-dd HH:mm')
      const pretties = pretty.split(' ')
      this.activity.date = pretties[0]
      this.activity.time = pretties[1]
    },
    fetchGoal() {
      return this.$axios.get(`/goals/${this.goalId}/`).then(res => {
        this.goal = res.data
      })
    },
    registerActivity(requestBody) {
      this.$store.commit('startLoading')
      this.$axios.post('/activities/activity_results/', requestBody).then(() => {
        this.$store.commit('notify/set', {message: '活動を追加しました。'})
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.push(`/members/${this.memberId}?goalId=${this.goalId}`)
        })
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
    registerDraft(requestBody) {
      this.$store.commit('startLoading')
      this.$axios.post(`/goals/${this.goalId}/activity_drafts/`, requestBody).then(() => {
        this.$store.commit('notify/set', {message: '下書きを追加しました。'})
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.push(`/members/${this.memberId}?goalId=${this.goalId}`)
        })
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
  }
}
</script>

<style lang="scss" scoped>

.edit-head{
  background-color: $l-blue;
  @media screen and (min-width:992px) {
    width: calc(100% - 422px);
    right: 0;
    left: auto;
  }
  &__icon{
    height: 22px;
    width: auto;
    @media screen and (max-width:320px) {
      height: 18px;
    }
  }
  &__heading{
    color: $white;
    font-size: $fs-lg;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
  &__activity{
    position: relative;
    font-size: $fs-nm;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
}

.form-check {
  position:relative;
}

input[type="checkbox"] {
  display:none;
}

.form-check-label {
  display: block;
  // padding: 10px 10px 10px 40px;
  cursor:pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: $white;
}

.form-check-label:after {
  content: "";
  width: 20px;
  height: 20px;
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  top: 50%;
  margin: -10px 8px;
  background-color: $grey16;
  border-radius: 50%;
}

input[type="checkbox"]:checked + .form-check-label:after {
  background-color: $pink;
}

.form-group {
  .conditions {
    gap: 10px 10px;
    flex-wrap: wrap;
  }
  .condition {
    width: calc(100% / 4 - 10px * (3 / 4));

    &__label {
      margin-bottom: 5px;
    }

    &__input {
      background-color: $white;
      color: $l-blue;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;

      .input-item {
        background: none;
        color: $l-blue;
        border: none;
        width: 100%;
        height: 100%;
        text-align: center;
        text-align-last: center;
      }

      .select-2-item {
        width: 100%;
        height: 100%;
        text-align: center;
        display: flex;
        justify-content: center;

        select {
          background: none;
          color: $l-blue;
          border: none;
          height: 100%;
          text-align-last: center;
          padding: 0;
          appearance: none;
        }
      }
    }
  }
  .condition-error-message {
    margin-top: 10px;
    color: $primary;

    p {
      line-height: 150%;
    }
  }
}
</style>